<template>
  <div class="map-container" v-if="loaded">
    <div class="search">
      <input type="text" v-model="search" @keydown="debouncedCourseSearch()" />

      <div class="results">
        <slide-up-down :active="searchResults.length > 0" :duration="300">
          <ul>
            <li v-for="result in searchResults" @click="viewCourse(result)">
              {{ result.name }}
            </li>
          </ul>
        </slide-up-down>
      </div>
    </div>
    <div id="map" class="map-canvas"></div>
  </div>
</template>

<script>
import 'leaflet/dist/leaflet.css'
import 'leaflet-geometryutil'
import '@maplibre/maplibre-gl-leaflet'
import L from 'leaflet'
import 'leaflet.locatecontrol'
import 'leaflet.fullscreen'
import mapBoxConfig from '@/mixins/mapBoxConfig.js'
import locationMixin from '@/mixins/locationMixin.js'
import { debounce } from 'lodash'
import SlideUpDown from 'vue-slide-up-down'

export default {
  name: 'PublicCourseMapSearch',
  mixins: [mapBoxConfig, locationMixin],
  components: {
    SlideUpDown,
  },
  watch: {
    loaded: function (val) {
      if (val) {
        this.$nextTick(() => {
          this.initMap()
        })
      }
    },
  },
  data() {
    return {
      search: '',
      searchResults: [],
      selectedCourse: null,
      loaded: false,
      usersPosition: null,
      zoom: 3,
      map: null,
      mapBounds: null,
      courses: [],
      clusters: [],
    }
  },

  computed: {},
  methods: {
    debouncedCourseSearch: debounce(
      function () {
        this.courseSearch()
      },
      500,
      { maxWait: 800 }
    ),
    viewCourse(course) {
      this.selectedCourse = course
      this.map.setView(
        [course.geolocation.lat, course.geolocation.lng],
        this.zoom,
        {
          animate: true,
          duration: 1,
          noMoveStart: true,
        }
      )
    },
    async courseSearch(first = 10) {
      if (this.search.length < 3) {
        this.searchResults = []
        return
      }
      let courses = await this.$axios({
        headers: { Authorization: this.$store.state.user.sessionInfo.token },
        data: {
          query: `
          {
            courses(first: ${first}, filter:{ search: "${this.search}" })
            {
              id
              name
              address
              type
              geolocation{
                lng
                lat
              }
              layouts{
                type
                id
                name
                difficulty
                payToPlay
                course{
                  name
                  id
                }
              }
            }
          }
          `,
        },
      })

      this.searchResults = courses.data.data.courses
    },
    debouncedLoadData: debounce(function () {
      this.getBoundingBox()
      this.zoom = this.map.getZoom()
      this.loadData()
    }, 500),
    getBoundingBox() {
      const bounds = this.map.getBounds()
      this.mapBounds = {
        minLat: bounds.getSouthWest().lat,
        maxLat: bounds.getNorthEast().lat,
        minLon: bounds.getSouthWest().lng,
        maxLon: bounds.getNorthEast().lng,
      }
    },
    initMap() {
      const usersPosition = [this.usersPosition.lat, this.usersPosition.lng]

      this.map = L.map('map', {
        center: usersPosition,
        zoom: this.zoom,
        minZoom: 1,
        zoomControl: false,
      })

      L.control
        .zoom({
          position: 'bottomleft',
        })
        .addTo(this.map)

      L.maplibreGL({
        style: 'https://tiles.openfreemap.org/styles/liberty',
        attribution: 'OpenFreeMap © OpenMapTiles Data from OpenStreetMap',
      }).addTo(this.map)

      L.control
        .fullscreen({
          position: 'topleft',
          pseudoFullscreen: true,
        })
        .addTo(this.map)

      this.getBoundingBox()
      this.loadData()

      this.map.on('zoomend', this.debouncedLoadData)

      // Listen for move end (when the user stops dragging the map) and get bounding box
      this.map.on('moveend', this.debouncedLoadData)
    },
    async loadData() {
      const layoutInfo = await this.$axios({
        headers: {
          Authorization: this.$store.state.user.sessionInfo.token,
        },
        data: {
          query: `
          {
            courseMapSearch(zoomLevel:${this.zoom},minLat: ${this.mapBounds.minLat}, maxLat: ${this.mapBounds.maxLat}, minLon: ${this.mapBounds.minLon}, maxLon: ${this.mapBounds.maxLon}) {
              ... on SearchCourseCluster {
               courseCount
                geolocation{
                  lat
                  lng
                }
              }
              ... on SearchCourse {
                id
                name
                type
                geolocation{
                  lat
                  lng
                }
              }
            }
          }
          `,
        },
      })

      this.clusters = layoutInfo.data.data.courseMapSearch.filter(
        (result) => result.courseCount
      )

      this.courses = layoutInfo.data.data.courseMapSearch.filter(
        (result) => result.name
      )

      // remove existing markers
      this.map.eachLayer((layer) => {
        if (layer instanceof L.Marker) {
          this.map.removeLayer(layer)
        }
      })

      this.clusters.forEach((cluster) => {
        const marker = L.marker([
          cluster.geolocation.lat,
          cluster.geolocation.lng,
        ])
        marker.addTo(this.map)
        marker.bindPopup(`<b>${cluster.courseCount} courses</b>`)
      })

      this.courses.forEach((course) => {
        if (course.id === this.selectedCourse?.id) {
          const marker = L.marker(
            [course.geolocation.lat, course.geolocation.lng],
            { opacity: 0.25 }
          )
          marker.addTo(this.map)
          marker.bindPopup(`<b>${course.name}</b>`)
        } else {
          const marker = L.marker([
            course.geolocation.lat,
            course.geolocation.lng,
          ])
          marker.addTo(this.map)
          marker.bindPopup(`<b>${course.name}</b>`)
        }
      })
    },
  },
  mounted() {},
  async beforeMount() {
    try {
      this.usersPosition = await this.getLocation(false)
      this.loaded = true
    } catch (err) {
      if (err.code == 1) {
        this.$store.dispatch('showMessage', {
          show: true,
          message: 'Location services disabled',
          type: 'error',
        })
      }
    }
  },
  destroyed() {},
}
</script>
<style lang="scss">
@import '@/styles/global-course';
</style>
<style lang="scss" scoped>
@import '@/styles/Typography';
.search {
  position: relative;
  z-index: 1;
  margin-bottom: 18px;
  input {
    padding: 8px 12px;
  }
}
.results {
  position: absolute;
  top: calc(100% + 12px);
  left: 0;

  ul {
    background: white;
    padding: 12px;
  }

  li {
    cursor: pointer;
    padding: 6px;
    border-bottom: 1px solid #ccc;
  }
}
.map-canvas {
  z-index: 0;
  border-radius: 12px;
  height: min(30vh, 400px);
  width: 100%;
  position: relative;
}
</style>
